/**
 * Configurations Store
 *
 * Manages Pre-Built and custom configurations.
 * System configurations are seeded by migration 280 and are read-only.
 * Custom configurations can be created by workspace admins.
 *
 * All reads go through get_configurations_for_workspace RPC.
 * Writes go through direct insert (custom only — system configs are immutable via RLS).
 */

import { create } from 'zustand';
import { getSafeSupabaseClient } from '../../lib/supabaseSync';
import type { Configuration } from '../../types/prioritization/configuration';

/** Configuration row augmented with workspace-level visibility (from get_workspace_config_visibility RPC) */
export interface ConfigurationWithVisibility extends Configuration {
  is_visible: boolean;
}

interface ConfigurationsState {
  configurations: Configuration[];
  isLoading: boolean;
  error: string | null;
  lastFetchedWorkspaceId: string | null;

  // Visibility settings (for the settings page — includes hidden configs)
  allConfigsWithVisibility: ConfigurationWithVisibility[];
  visibilityLoading: boolean;

  // Actions
  fetchConfigurations: (workspaceId: string) => Promise<Configuration[]>;
  fetchConfigurationBySlug: (slug: string, workspaceId: string) => Promise<Configuration | null>;
  getSystemConfiguration: (slug: string) => Configuration | null;
  createCustomConfiguration: (
    config: Omit<Configuration, 'id' | 'created_at' | 'updated_at' | 'is_system'> & { workspace_id: string }
  ) => Promise<Configuration | null>;
  archiveCustomConfiguration: (id: string) => Promise<boolean>;
  getConfigurationById: (id: string) => Configuration | null;
  clearError: () => void;

  // Visibility management (settings page)
  fetchAllWithVisibility: (workspaceId: string) => Promise<void>;
  toggleVisibility: (workspaceId: string, configId: string, isVisible: boolean, actorId?: string) => Promise<boolean>;
}

export const useConfigurationsStore = create<ConfigurationsState>((set, get) => ({
  configurations: [],
  isLoading: false,
  error: null,
  lastFetchedWorkspaceId: null,
  allConfigsWithVisibility: [],
  visibilityLoading: false,

  fetchConfigurations: async (workspaceId: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) {
      set({ error: 'Supabase client not available' });
      return [];
    }

    set({ isLoading: true, error: null });

    try {
      const { data, error } = await supabase
        .rpc('get_configurations_for_workspace', { p_workspace_id: workspaceId });

      if (error) {
        set({ isLoading: false, error: error.message });
        return [];
      }

      const configs = (data ?? []) as Configuration[];
      set({ configurations: configs, isLoading: false, lastFetchedWorkspaceId: workspaceId });
      return configs;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error fetching configurations';
      set({ isLoading: false, error: message });
      return [];
    }
  },

  fetchConfigurationBySlug: async (slug: string, workspaceId: string) => {
    const { configurations, fetchConfigurations } = get();

    // Check cache first
    const cached = configurations.find((c) => c.slug === slug);
    if (cached) return cached;

    // Fetch from server if not in cache
    const fetched = await fetchConfigurations(workspaceId);
    return fetched.find((c) => c.slug === slug) ?? null;
  },

  getSystemConfiguration: (slug: string) => {
    return get().configurations.find((c) => c.is_system && c.slug === slug) ?? null;
  },

  getConfigurationById: (id: string) => {
    return get().configurations.find((c) => c.id === id) ?? null;
  },

  createCustomConfiguration: async (config) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) {
      set({ error: 'Supabase client not available' });
      return null;
    }

    set({ isLoading: true, error: null });

    try {
      const { data, error } = await supabase
        .from('configurations')
        .insert({
          ...config,
          is_system: false,
        })
        .select()
        .single();

      if (error) {
        set({ isLoading: false, error: error.message });
        return null;
      }

      const newConfig = data as Configuration;
      set((state) => ({
        configurations: [...state.configurations, newConfig],
        isLoading: false,
      }));
      return newConfig;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error creating configuration';
      set({ isLoading: false, error: message });
      return null;
    }
  },

  archiveCustomConfiguration: async (id: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return false;
    const { error } = await supabase
      .from('configurations')
      .update({ status: 'archived' })
      .eq('id', id)
      .eq('is_system', false);
    if (error) { set({ error: error.message }); return false; }
    set((state) => ({
      configurations: state.configurations.filter((c) => c.id !== id),
      allConfigsWithVisibility: state.allConfigsWithVisibility.filter((c) => c.id !== id),
    }));
    return true;
  },

  clearError: () => set({ error: null }),

  fetchAllWithVisibility: async (workspaceId: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    set({ visibilityLoading: true });
    try {
      const { data, error } = await supabase
        .rpc('get_workspace_config_visibility', { p_workspace_id: workspaceId });
      if (error) { set({ visibilityLoading: false, error: error.message }); return; }
      set({ allConfigsWithVisibility: (data ?? []) as ConfigurationWithVisibility[], visibilityLoading: false });
    } catch (err) {
      set({ visibilityLoading: false, error: err instanceof Error ? err.message : 'Unknown error' });
    }
  },

  toggleVisibility: async (workspaceId: string, configId: string, isVisible: boolean, actorId?: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return false;
    const { error } = await supabase.rpc('toggle_workspace_config_visibility', {
      p_workspace_id:     workspaceId,
      p_configuration_id: configId,
      p_is_visible:       isVisible,
      p_actor_id:         actorId ?? null,
    });
    if (error) { set({ error: error.message }); return false; }
    // Update local state
    set((state) => ({
      allConfigsWithVisibility: state.allConfigsWithVisibility.map((c) =>
        c.id === configId ? { ...c, is_visible: isVisible } : c
      ),
    }));
    // If hiding, also remove from the active configurations list; if showing, refetch
    if (!isVisible) {
      set((state) => ({ configurations: state.configurations.filter((c) => c.id !== configId) }));
    } else {
      void get().fetchConfigurations(workspaceId);
    }
    return true;
  },
}));
